import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import { X, CaretDown, Globe } from '@phosphor-icons/react'
import './MerchantDetailsPage.scss'
import merchantApi from '../../services/merchantApi'
import Header from '../../components/Header/Header'
import moment from 'moment'
import { Box, Grid, Divider } from '@mui/material'
import PhoneInputField from '../../components/PhoneInputField/PhoneInputField'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { Icon } from '@iconify/react/dist/iconify.js'
import AppTable from '../../components/AppTable/AppTable'
import { merchantCatalogColumns, merchantOrderColumns, merchantPaymentColumns, merchantReservationColumns } from './MerchantsColumn'
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown'
import AppPagination from '../../components/AppPagination/AppPagination'

const moduleCode = 'outlet'

const DEFAULT_OUTLET_PAGE_SIZE = 20

const getPhoneParts = (value = '') => {
    const raw = String(value || '').trim()
    const match = raw.match(/^(\+\d{2,4})/)

    return {
        countryCode: match?.[1] || '+60',
        number: raw.replace(/^(\+\d{2,4})/, '').replace(/\D/g, ''),
    }
}

const normalizeOutletList = (response) => {
    const responseData = response?.data
    const outlets = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
            ? responseData.data
            : []
    const meta = response?.meta || responseData?.meta || {}

    return {
        currentPage: Number(meta.current_page || meta.page || 1),
        totalPages: Number(meta.last_page || meta.total_pages || 1),
        total: Number(meta.total ?? outlets.length),
        from: Number(meta.from ?? (outlets.length ? 1 : 0)),
        to: Number(meta.to ?? outlets.length),
        perPage: Number(meta.per_page || DEFAULT_OUTLET_PAGE_SIZE),
        outlets: outlets.map((outlet) => ({
            id: outlet.id,
            code: outlet.outlet_code || outlet.code || outlet.outlet_ref_id || `OUTLET-${outlet.id}`,
            name: outlet.name || 'Untitled outlet',
            status: outlet.status_label || outlet.status || 'Closed',
            address: outlet.address || '-',
            tables: Number(outlet.table_count || 0),
            maxPax: Number(outlet.max_pax || 0),
            pos_config: outlet.pos_config,
            hours: Array.isArray(outlet.opening_hours || outlet.service_hours)
                ? (outlet.opening_hours || outlet.service_hours).map((hour) => ({
                    day: hour.day || `Day ${hour.day_of_week}`,
                    time: hour.status === 'closed' || hour.is_closed
                        ? 'Closed'
                        : hour.display || `${hour.open_time || '--'} - ${hour.close_time || '--'}`,
                }))
                : [],
        })),
    }
}

const normalizeMerchantDetail = (response) => {
    const merchant = response?.data || {}

    return {
        ...merchant,
        business_name: merchant.name,
        address_line_1: merchant.address,
        postal_code: merchant.postcode,
        logo: merchant.logo_url,
        created_at: merchant.joined_at,
    }
}

const normalizeCollection = (response) => {
    const responseData = response?.data
    const rows = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
            ? responseData.data
            : []
    const meta = response?.meta || responseData?.meta || {}

    return {
        rows,
        pagination: {
            current_page: Number(meta.current_page || 1),
            last_page: Number(meta.last_page || 1),
            per_page: Number(meta.per_page || 20),
            total: Number(meta.total ?? rows.length),
            from: Number(meta.from ?? (rows.length ? 1 : 0)),
            to: Number(meta.to ?? rows.length),
        },
    }
}

function DisplayField({ label, value, badge }) {
    return (
        <div className="merchant-details-display-field">
            <span className="merchant-details-display-field__label">{label}</span>
            {badge ? (
                <div className="merchant-details-display-field__value">
                    <span className="merchant-details-badge-id">{badge}</span>
                </div>
            ) : (
                <p className="merchant-details-display-field__value">{value || '-'}</p>
            )}
        </div>
    )
}

function EditMerchantDrawer({ onClose, merchantData, onSaved, stateOptions, countryOptions }) {
    const fileInputRef = useRef(null)
    const [form, setForm] = useState({
        business_name: '',
        business_registration_no: '',
        business_email: '',
        business_phone: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        state_id: '',
        country_id: '',
        owner_name: '',
        owner_email: '',
        owner_phone: '',
    })
    const [, setLogoFile] = useState(null)
    const [logoPreviewUrl, setLogoPreviewUrl] = useState('')
    const [submitting] = useState(false)
    const [error, setError] = useState('')
    const [fieldError] = useState('')
    const [businessCountryCode, setBusinessCountryCode] = useState('+60')
    const [ownerCountryCode, setOwnerCountryCode] = useState('+60')

    useEffect(() => {
        if (!merchantData) return

        const businessPhone = parsePhoneNumberFromString(merchantData.business_phone ? `+${merchantData.business_phone}` : '')
        const ownerPhone = parsePhoneNumberFromString(merchantData.owner_phone ? `+${merchantData.owner_phone}` : '')

        setBusinessCountryCode(businessPhone?.countryCallingCode || '+60')
        setOwnerCountryCode(ownerPhone?.countryCallingCode || '+60')

        setForm({
            business_name: merchantData.business_name || '',
            business_registration_no: merchantData.business_registration_no || '',
            business_email: merchantData.business_email || '',
            business_phone: businessPhone?.nationalNumber,
            address_line_1: merchantData.address_line_1 || '',
            address_line_2: merchantData.address_line_2 || '',
            city: merchantData.city || '',
            postal_code: merchantData.postal_code || '',
            state_id: merchantData.state_id || '',
            country_id: merchantData.country_id || '',
            owner_name: merchantData.owner_name || '',
            owner_email: merchantData.owner_email || '',
            owner_phone: ownerPhone?.nationalNumber,
        })
    }, [merchantData])

    useEffect(() => {
        return () => {
            if (logoPreviewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(logoPreviewUrl)
            }
        }
    }, [logoPreviewUrl])

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }))
    }

    const handleLogoPick = (event) => {
        const file = event.target.files?.[0]
        if (!file) return

        if (logoPreviewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(logoPreviewUrl)
        }

        setLogoFile(file)
        setLogoPreviewUrl(URL.createObjectURL(file))
        event.target.value = ''
    }

    const handleSave = async () => {
        if (submitting) return

        setError('Updating merchant details is not supported by the current Admin API.')
    }

    const defaultLogoUrl = merchantData?.logo?.url || merchantData?.logo_image_url || merchantData?.logo || ''
    const previewLogoUrl = logoPreviewUrl || defaultLogoUrl
    console.log("fieldError", fieldError)
    return createPortal(
        <div className="merchant-details-drawer-overlay" onClick={onClose}>
            <div className="merchant-details-drawer" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="merchant-details-drawer__header">
                    <h3>Merchant details</h3>
                    <button className="merchant-details-drawer__close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Content Body */}
                <div className="merchant-details-drawer__body">
                    {error && <div className="sr-error">{error}</div>}
                    <div className="merchant-details-drawer__section-title">BASIC INFO</div>

                    {/* Business Name */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Business name</label>
                        <input className="sr-input" type="text" value={form.business_name} onChange={(e) => handleChange('business_name', e.target.value)} />
                        {fieldError?.business_name && <span className="add-outlet-field__error">{fieldError.business_name}</span>}
                    </div>

                    {/* Registration No */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">
                            Business registration no. <span className="merchant-details-field__optional">(optional)</span>
                        </label>
                        <input className="sr-input" type="text" value={form.business_registration_no} onChange={(e) => handleChange('business_registration_no', e.target.value)} />
                        {fieldError?.business_registration_no && <span className="add-outlet-field__error">{fieldError.business_registration_no}</span>}
                    </div>

                    {/* Email */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Business email address</label>
                        <input className="sr-input" type="email" value={form.business_email} onChange={(e) => handleChange('business_email', e.target.value)} />
                        {fieldError?.business_email && <span className="add-outlet-field__error">{fieldError.business_email}</span>}
                    </div>

                    {/* Phone Number */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Business phone number</label>
                        {/* <div className="sr-phone-input">
              <span className="sr-phone-input__prefix">+60</span>
              <input type="text" value={form.business_phone} onChange={(e) => handleChange('business_phone', e.target.value)} />
            </div> */}
                        <PhoneInputField
                            value={form.business_phone}
                            onChange={(value) => handleChange('business_phone', value)}
                            countryCode={businessCountryCode}
                            onCountryCodeChange={setBusinessCountryCode}
                        />
                        {fieldError?.business_phone && <span className="add-outlet-field__error">{fieldError.business_phone}</span>}
                    </div>

                    {/* Logo Upload */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">
                            Merchant logo <span className="merchant-details-field__optional">(optional)</span>
                        </label>
                        <div className="merchant-details-logo-inline">
                            <div className="merchant-details-logo-inline__preview">
                                {previewLogoUrl ? (
                                    <img src={previewLogoUrl} alt="Merchant logo" className="mock-logo-circle" />
                                ) : (
                                    <div className="mock-logo-circle">
                                        <span style={{ fontSize: '18px', color: '#004348', fontWeight: 'bold' }}>H</span>
                                    </div>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                style={{ display: 'none' }}
                                onChange={handleLogoPick}
                            />
                            <button
                                className="merchant-details-btn merchant-details-btn--outline merchant-details-btn--sm"
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Upload
                            </button>
                        </div>
                    </div>

                    {/* Address Line 1 */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Address line 1</label>
                        <input className="sr-input" type="text" value={form.address_line_1} onChange={(e) => handleChange('address_line_1', e.target.value)} />
                        {fieldError?.address_line_1 && <span className="add-outlet-field__error">{fieldError.address_line_1}</span>}
                    </div>

                    {/* Address Line 2 */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">
                            Address line 2 <span className="merchant-details-field__optional">(optional)</span>
                        </label>
                        <input className="sr-input" type="text" placeholder="e.g. Apt / Suite" value={form.address_line_2} onChange={(e) => handleChange('address_line_2', e.target.value)} />
                        {fieldError?.address_line_2 && <span className="add-outlet-field__error">{fieldError.address_line_2}</span>}
                    </div>

                    {/* City */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">City</label>
                        <input className="sr-input" type="text" value={form.city} onChange={(e) => handleChange('city', e.target.value)} />
                        {fieldError?.city && <span className="add-outlet-field__error">{fieldError.city}</span>}
                    </div>

                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Postal code</label>
                        <input className="sr-input" type="text" value={form.postal_code} onChange={(e) => handleChange('postal_code', e.target.value)} />
                        {fieldError?.postal_code && <span className="add-outlet-field__error">{fieldError.postal_code}</span>}
                    </div>

                    {/* State */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">State</label>
                        <div className="merchant-details-select">
                            <select value={form.state_id || ''} onChange={(e) => handleChange('state_id', e.target.value)} placeholder="Select state">
                                <option key={'none'} value={''}>Select state</option>
                                {
                                    stateOptions?.map((state) => (
                                        <option key={state.id} value={state.id}>{state.name}</option>
                                    ))
                                }
                            </select>
                            <CaretDown size={16} className="merchant-details-select__icon" />
                            {fieldError?.state_id && <span className="add-outlet-field__error">{fieldError.state_id}</span>}
                        </div>
                    </div>
                    {/* Country */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Country</label>
                        <div className="merchant-details-select merchant-details-select__disabled">
                            <select value={form.country_id || ''} onChange={(e) => handleChange('country_id', e.target.value)} placeholder="Select country" disabled={true}>
                                <option key={'none'} value={''}>Select country</option>
                                {
                                    countryOptions?.map((country) => (
                                        <option key={country.id} value={country.id}>{country.name}</option>
                                    ))
                                }
                            </select>
                            <CaretDown size={16} className="merchant-details-select__icon" />
                            {fieldError?.country_id && <span className="add-outlet-field__error">{fieldError.country_id}</span>}
                        </div>
                    </div>
                    <div className="merchant-details-drawer__section-title">OWNER INFORMATION</div>
                    {/* Name */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Name</label>
                        <input className="sr-input" type="text" value={form.owner_name} onChange={(e) => handleChange('owner_name', e.target.value)} />
                        {fieldError?.owner_name && <span className="add-outlet-field__error">{fieldError.owner_name}</span>}
                    </div>
                    {/* Phone Number */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Phone number</label>
                        <PhoneInputField
                            value={form.owner_phone}
                            onChange={(value) => handleChange('owner_phone', value)}
                            countryCode={ownerCountryCode}
                            onCountryCodeChange={setOwnerCountryCode}
                        />
                        {fieldError?.owner_phone && <span className="add-outlet-field__error">{fieldError.owner_phone}</span>}
                    </div>
                    {/* Email */}
                    <div className="merchant-details-field">
                        <label className="merchant-details-field__label">Email</label>
                        <input className="sr-input" type="email" value={form.owner_email} onChange={(e) => handleChange('owner_email', e.target.value)} />
                        {fieldError?.owner_email && <span className="add-outlet-field__error">{fieldError.owner_email}</span>}
                    </div>
                </div>

                {/* Footer */}
                <div className="merchant-details-drawer__footer">
                    <button className="merchant-details-btn merchant-details-btn--ghost" type="button" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button className="merchant-details-btn merchant-details-btn--dark merchant-details-btn--lg" type="button" onClick={handleSave} disabled={submitting}>{submitting ? 'Saving...' : 'Save changes'}</button>
                </div>
            </div>
        </div>,
        document.body
    )
}

// ── Tab 1: Overview ──────────────────────────────────────────────────────────
function OverviewTab({ onEditMerchant, merchantData }) {
    console.log("OverviewTab merchantData", merchantData)
    return (
        <div className="merchant-details-subcontainer">
            <div className="merchant-details-section-header">
                <h2>Merchant Information</h2>
                {/* <button className="merchant-details-btn merchant-details-btn--dark" onClick={onEditMerchant}>
                    Edit merchant
                </button> */}
            </div>
            <Grid container spacing={2}>
                <Grid item lg={6} md={12} sm={12} xs={12}>
                    <div className="merchant-details-card">
                        <div className="merchant-details-card__header">Business Details</div>
                        <div className="merchant-details-card__body merchant-details-grid-2-inner">
                            <DisplayField label="Merchant ID" badge={merchantData?.merchant_code} />
                            <DisplayField label="Business name" value={merchantData?.business_name} />
                            <DisplayField label="Business registration no." value={merchantData?.business_registration_no} />
                            <DisplayField label="Business email" value={merchantData?.business_email} />
                            <DisplayField label="Business phone number" value={`${merchantData?.business_phone || ''}`} />
                            <DisplayField label="Address" value={`${merchantData?.address_line_1 || ''}, ${merchantData?.address_line_2 || ''}`} />
                        </div>
                    </div>
                </Grid>
                <Grid item lg={6} md={12} sm={12} xs={12}>
                    <div className="merchant-details-card">
                        <div className="merchant-details-card__header">Owner Information</div>
                        <div className="merchant-details-card__body merchant-details-grid-2-inner">
                            <DisplayField label="Owner" value={merchantData?.owner_name || '-'} />
                            <DisplayField label="Email" value={merchantData?.owner_email || '-'} />
                            <DisplayField label="Phone number" value={`${merchantData?.owner_phone || '-'}`} />
                            <DisplayField label="Member since" value={merchantData?.created_at ? moment(merchantData?.created_at).format('MMMM D, YYYY') : '-'} />
                        </div>
                    </div>
                </Grid>
            </Grid>
        </div>
    )
}

function OutletIntegrationSettings({ outlet }) {
    const outletWaveConfig = outlet?.wave_api_integration || {}
    const outletSyncSettings = outlet?.sync_settings || {}

    const [credentials, setCredentials] = useState({
        tokenEndpoint: outletWaveConfig?.token_endpoint || '',
        grantType: outletWaveConfig?.grant_type || '',
        clientId: outletWaveConfig?.client_id || '',
        clientSecret: outletWaveConfig?.client_secret || '',
    })
    const [posSystem, setPosSystem] = useState(outletSyncSettings?.pos_system || '')
    const [autoSync, setAutoSync] = useState(outletSyncSettings?.auto_sync_catalog ?? false)
    const [showSecret, setShowSecret] = useState(false)

    const updateCredential = (field, value) => {
        setCredentials((current) => ({ ...current, [field]: value }))
    }

    return (
        <div className="merchant-outlet-settings">
            <section className="merchant-details-card merchant-outlet-settings__card">
                <div className="merchant-details-card__header">WAVE API credentials</div>
                <div className="merchant-outlet-settings__body">
                    <label className="merchant-details-field">
                        <span className="merchant-details-field__label">Token endpoint</span>
                        <input
                            className="merchant-details-input "
                            value={credentials.tokenEndpoint}
                            onChange={(event) => updateCredential('tokenEndpoint', event.target.value)}
                            disabled
                        />
                    </label>

                    <label className="merchant-details-field">
                        <span className="merchant-details-field__label">Grant type</span>
                        <input
                            className="merchant-details-input"
                            value={credentials.grantType}
                            onChange={(event) => updateCredential('grantType', event.target.value)}
                            disabled
                        />
                    </label>

                    <label className="merchant-details-field">
                        <span className="merchant-details-field__label">Client ID</span>
                        <input
                            className="merchant-details-input"
                            value={credentials.clientId}
                            onChange={(event) => updateCredential('clientId', event.target.value)}
                            disabled
                        />
                    </label>

                    <label className="merchant-details-field">
                        <span className="merchant-details-field__label">Client secret</span>
                        <div className="merchant-outlet-settings__secret">
                            <input
                                className="merchant-details-input"
                                type={showSecret ? 'text' : 'password'}
                                value={credentials.clientSecret}
                                onChange={(event) => updateCredential('clientSecret', event.target.value)}
                                disabled
                            />
                            {/* <button type="button" onClick={() => setShowSecret((visible) => !visible)} aria-label={showSecret ? 'Hide client secret' : 'Show client secret'} disabled={true}>
                                <Icon icon={showSecret ? 'ph:eye-slash' : 'ph:eye'} height={18} />
                            </button> */}
                        </div>
                    </label>

                    {/* <button type="button" className="merchant-details-btn merchant-details-btn--dark merchant-outlet-settings__save">Save</button> */}
                </div>
            </section>

            <section className="merchant-details-card merchant-outlet-settings__card">
                <div className="merchant-details-card__header">Sync settings</div>
                <div className="merchant-outlet-settings__body">
                    <label className="merchant-details-field">
                        <span className="merchant-details-field__label">POS system</span>
                        <div className="merchant-details-select">
                            <select value={posSystem} onChange={(event) => setPosSystem(event.target.value)} disabled>
                                <option value="Lightspeed">Lightspeed</option>
                                <option value="Square">Square</option>
                                <option value="StoreHub">StoreHub</option>
                                <option value="Other">Other</option>
                            </select>
                            <CaretDown className="merchant-details-select__icon" size={16} />
                        </div>
                    </label>

                    <div className="merchant-outlet-settings__sync-row">
                        <div>
                            <span className="merchant-details-field__label">Auto-sync catalog</span>
                            <span className="merchant-outlet-settings__hint">Sync every {outletSyncSettings?.sync_interval_minutes ?? '-'} minutes</span>
                        </div>
                        <label className="merchant-outlet-settings__switch">
                            <input type="checkbox" checked={autoSync} onChange={(event) => setAutoSync(event.target.checked)} disabled/>
                            <span aria-hidden="true" />
                            <span className="merchant-outlet-settings__sr-only">Auto-sync catalog</span>
                        </label>
                    </div>

                    <div className="merchant-outlet-settings__sync-info">
                        <span className="merchant-outlet-settings__hint">Items sync</span>
                        <span>{outletSyncSettings?.items_synced_count ?? '-'} items&nbsp; • &nbsp;last sync at {outletSyncSettings?.last_sync_at ?? '-'} </span>
                    </div>

                    {/* <button type="button" className="merchant-details-btn merchant-details-btn--dark merchant-outlet-settings__save">Save</button> */}
                </div>
            </section>
        </div>
    )
}

// ── Tab 2: Outlets ───────────────────────────────────────────────────────────
function OutletsTab({ onAddOutlet, handlePageChange, currentPage, totalPages, outletList, navigate, loading, editable }) {
    if (loading) {
        return (
            <div className="merchant-details-subcontainer">
                <div className="merchant-details-loading">Loading outlets...</div>
            </div>
        )
    }

    const outlets = outletList?.outlets || []
    const from = outletList?.from 
    const to = outletList?.to 
    const total = outletList?.total

    return (
        <div className="merchant-details-subcontainer">
            <div className="merchant-details-section-header">
                <h2>Outlets</h2>
                {/* <button className="merchant-details-btn merchant-details-btn--dark" onClick={onAddOutlet}>Add outlet</button> */}
            </div>

            <div className="merchant-details-outlet-list">
                {outlets.map((outlet) => (
                    <div key={outlet.id} className="merchant-details-card merchant-details-outlet-card">
                        <div className="merchant-details-outlet-card__header">
                            <div className="merchant-details-outlet-card__title-area">
                                <span className="merchant-details-badge-id">{outlet.code || outlet.id}</span>
                                <h3>{outlet.name}</h3>
                            </div>
                        </div>

                        <div className="merchant-details-outlet-card__body">
                            <div className="merchant-details-grid-2-inner">
                                <div className="merchant-details-display-field">
                                    <span className="merchant-details-display-field__label">Status</span>
                                    <div className="merchant-details-badge-status"><span className="dot" />{outlet.status ? outlet.status[0].toUpperCase() + outlet.status.slice(1) : '-'}</div>
                                </div>
                                <DisplayField label="Address" value={outlet.address} />
                                <DisplayField label="Table" value={outlet.tables || 0} />
                                <DisplayField label="Max pax" value={outlet.maxPax || 0} />
                            </div>

                            <div className="merchant-details-outlet-hours">
                                <p className="merchant-details-outlet-hours__label">Service hours</p>
                                <div className="merchant-details-hours-grid">
                                    {outlet.hours.map((h, index) => (
                                        <div key={index} className="merchant-details-hours-bubble">
                                            <span className="merchant-details-hours-bubble__day">{h.day}</span>
                                            <span className="merchant-details-hours-bubble__time">{h.time}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <OutletIntegrationSettings outlet={outlet} />
                        </div>
                    </div>
                ))}
            </div>

            {/* PAGINATION */}
            <AppPagination
                currentPage={currentPage}
                pagination={outletList?.pagination}
                onPageChange={handlePageChange}
            />
        </div>
    )
}

function CatalogTab({
    filterValues,
    setFilterValues,
    searchQuery,
    setSearchQuery,
    rows,
    pagination,
    loading,
    onPageChange,
    categoryOptions,
    categoryStatusOptions
}) {
    return (
        <AppTable
            columns={merchantCatalogColumns}
            toolBar={
                <Box className="merchant-table-toolbar">
                    <Grid container spacing={2}>
                        <Grid item sm={8} xs={12}>
                            <Box className="flex-container">
                                <Box className="toolbar-search">
                                    <Icon icon="ph:magnifying-glass" height={18} />
                                    <input type="text" placeholder="Search items, dish name" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                </Box>
                                <FilterDropdown
                                    label={'categories'}
                                    options={categoryOptions || []}
                                    value={filterValues.category}
                                    onChange={(value) => setFilterValues({ ...filterValues, category: value })}
                                />
                                <FilterDropdown
                                    label={'statuses'}
                                    options={categoryStatusOptions || []}
                                    value={filterValues.status}
                                    onChange={(value) => setFilterValues({ ...filterValues, status: value })}
                                />
                            </Box>
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <Box className="flex-container flex-container__right merchant-details-page__subtitle">
                                {pagination?.total || 0} merchants
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            }
            rows={rows}
            pagination={pagination}
            loading={loading}
            onPageChange={onPageChange}
        />
    )
}

function OrdersTab({ filterValues, setFilterValues, searchQuery, setSearchQuery }) {
    return (
        <AppTable
            columns={merchantOrderColumns}
            toolBar={
                <Box className="merchant-table-toolbar">
                    <Grid container spacing={2}>
                        <Grid item sm={8} xs={12}>
                            <Box className="flex-container">
                                <Box className="toolbar-search">
                                    <Icon icon="ph:magnifying-glass" height={18} />
                                    <input type="text" placeholder="Search items, dish name" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                </Box>
                                <FilterDropdown
                                    label={'categories'}
                                    options={[]}
                                    value={filterValues.category}
                                    onChange={(value) => setFilterValues({ ...filterValues, category: value })}
                                />
                                <FilterDropdown
                                    label={'statuses'}
                                    options={[]}
                                    value={filterValues.status}
                                    onChange={(value) => setFilterValues({ ...filterValues, status: value })}
                                />
                            </Box>
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <Box className="flex-container flex-container__right merchant-details-page__subtitle">
                                12 merchants
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            }
            rows={[{ id: 2201, name: 'Yakiniku Curry', description: 'Pan fried beef with Japanese curry', category: 'BENTO', status: 'available', tax: 'included' }, { id: 2202, name: 'Desserts', description: 'Sweet treats', status: 'available' }]}
        />
    )
}

function ReservationTab({ filterValues, setFilterValues, searchQuery, setSearchQuery }) {
    return (
        <AppTable
            columns={merchantReservationColumns}
            toolBar={
                <Box className="merchant-table-toolbar">
                    <Grid container spacing={2}>
                        <Grid item sm={8} xs={12}>
                            <Box className="flex-container">
                                <Box className="toolbar-search">
                                    <Icon icon="ph:magnifying-glass" height={18} />
                                    <input type="text" placeholder="Search transactions" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                </Box>
                                <FilterDropdown
                                    label={'payment type'}
                                    options={[]}
                                    value={filterValues.paymentType}
                                    onChange={(value) => setFilterValues({ ...filterValues, paymentType: value })}
                                />
                                <FilterDropdown
                                    label={'statuses'}
                                    options={[]}
                                    value={filterValues.status}
                                    onChange={(value) => setFilterValues({ ...filterValues, status: value })}
                                />
                            </Box>
                        </Grid>
                        <Grid item sm={4} xs={12}>
                            <Box className="flex-container flex-container__right merchant-details-page__subtitle">
                                12 merchants
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            }
            rows={[{ id: 'RS-0041', name: 'Tan Wei Lun', contact: '+6012551225', pax: '3', unit_price: '59.60', tax: 'included' }]}
        />
    )
}

function PaymentTab({ filterValues, setFilterValues, searchQuery, setSearchQuery }) {
    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item sm={3} xs={12}>
                    <Box className="merchant-details-stat">
                        <div className="merchant-details-stat-label">DUITNOW GMV</div>
                        <div className="merchant-details-stat-value">RM {'0.00'}</div>
                        <div className="merchant-details-stat-meta">59%</div>
                    </Box>
                </Grid>
                <Grid item sm={3} xs={12}>
                    <Box className="merchant-details-stat">
                        <div className="merchant-details-stat-label">EWALLET GMV</div>
                        <div className="merchant-details-stat-value">RM {'0.00'}</div>
                        <div className="merchant-details-stat-meta">23% TNG / Boost</div>
                    </Box>
                </Grid>
                <Grid item sm={3} xs={12}>
                    <Box className="merchant-details-stat">
                        <div className="merchant-details-stat-label">CREDIT CARD GMV</div>
                        <div className="merchant-details-stat-value">RM {'0.00'}</div>
                        <div className="merchant-details-stat-meta">13% Visa / Master</div>
                    </Box>
                </Grid>
                <Grid item sm={3} xs={12}>
                    <Box className="merchant-details-stat">
                        <div className="merchant-details-stat-label">CASH GMV</div>
                        <div className="merchant-details-stat-value">RM {'0.00'}</div>
                        <div className="merchant-details-stat-meta">5%</div>
                    </Box>
                </Grid>
                <Grid item sm={12} my={1} className="flex-container merchant-details-stat-label">
                    Transaction <Divider style={{flexGrow: 1}} />
                </Grid>
                <Grid item sm={12}>
                    <AppTable
                        columns={merchantPaymentColumns}
                        toolBar={
                            <Box className="merchant-table-toolbar">
                                <Grid container spacing={2}>
                                    <Grid item sm={8} xs={12}>
                                        <Box className="flex-container">
                                            <Box className="toolbar-search">
                                                <Icon icon="ph:magnifying-glass" height={18} />
                                                <input type="text" placeholder="Search transactions" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                            </Box>
                                            <FilterDropdown
                                                label={'payment type'}
                                                options={[]}
                                                value={filterValues.paymentType}
                                                onChange={(value) => setFilterValues({ ...filterValues, paymentType: value })}
                                            />
                                            <FilterDropdown
                                                label={'statuses'}
                                                options={[]}
                                                value={filterValues.status}
                                                onChange={(value) => setFilterValues({ ...filterValues, status: value })}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item sm={4} xs={12}>
                                        <Box className="flex-container flex-container__right merchant-details-page__subtitle">
                                            12 merchants
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        }
                        rows={[{ id: 'ORD-0041', name: 'Tan Wei Lun', payment_type: 'duitnow', status: 'paid', amount: '59.60', created_at: '14 May 2026 11:03pm' }]}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}

function BrandingTab({ merchantData, brandingData, merchantId, onSaved, categoryOptions }) {
    const [primaryColour, setPrimaryColour] = useState(
        brandingData?.primary_colour || merchantData?.primary_colour || '#15B6CD'
    )
    const [logoFile, setLogoFile] = useState(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const merchantLogo = brandingData?.attachments?.find((item) => item?.collection === 'logo')?.source_url || merchantData?.logo || ''
    const customDomain = brandingData?.custom_domain || merchantData?.custom_domain || '-'
    console.log("BrandingTab merchantData", merchantData)
    console.log("BrandingTab brandingData", brandingData)
    useEffect(() => {
        setPrimaryColour(brandingData?.primary_colour || merchantData?.primary_colour || '#15B6CD')
    }, [brandingData, merchantData])
    console.log("merchantLogo", merchantLogo)
    const handleSave = async () => {
        setSaving(true)
        setError('')

        try {
            await merchantApi.merchants.branding.update(merchantId, {
                primary_colour: primaryColour,
                ...(logoFile ? { logo: logoFile } : {}),
            })
            setLogoFile(null)
            await onSaved?.()
        } catch (requestError) {
            setError(requestError?.message || 'Unable to update merchant branding.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="merchant-branding">
            <section className="merchant-details-card merchant-branding__card">
                <div className="merchant-details-card__header">Domain &amp; SSL</div>
                <div className="merchant-branding__domain-body">
                    <div className="merchant-branding__field">
                        <span className="merchant-branding__label">Custom domain</span>
                        <span className="merchant-branding__value">{customDomain}</span>
                        <div className="merchant-branding__chips">
                            {merchantData?.ssl_status && <span className="merchant-branding__status"><span className="dot" />SSL Valid</span>}
                            {merchantData?.dns_status && <span className="merchant-branding__status"><span className="dot" />DNS OK</span>}
                            {merchantData?.cname_status && <span className="merchant-branding__status"><span className="dot" />CNAME</span>}
                        </div>
                    </div>

                    <div className="merchant-branding__records">
                        <div className="merchant-branding__field">
                            <span className="merchant-branding__label">SPF record</span>
                            <span className={merchantData?.spf_status ? "merchant-branding__status merchant-branding__status--valid" : "merchant-branding__status"}><span className="dot" />{merchantData?.spf_status ? "Valid": "Invalid"}</span>
                        </div>
                        <div className="merchant-branding__field">
                            <span className="merchant-branding__label">DKIM record</span>
                            <span className={merchantData?.dkim_status ? "merchant-branding__status merchant-branding__status--valid" : "merchant-branding__status"}><span className="dot" />{merchantData?.dkim_status ? "Valid": "Invalid"}</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="merchant-details-card merchant-branding__card">
                <div className="merchant-details-card__header">Visual identity</div>
                <div className="merchant-branding__identity-body">
                    {error && <div className="sr-error">{error}</div>}
                    <div className="merchant-branding__logo-row">
                        <div className="merchant-branding__logo">
                            {logoFile || merchantLogo ? (
                                <img src={logoFile ? URL.createObjectURL(logoFile) : merchantLogo} alt="Merchant logo" />
                            ) : (
                                <Icon icon="ph:image-square" height={24} />
                            )}
                        </div>
                        <div className="merchant-branding__logo-copy">
                            <span className="merchant-branding__value merchant-branding__value--strong">Logo</span>
                            <label className="merchant-branding__label">
                                {logoFile ? logoFile.name : 'Choose a new logo'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(event) => setLogoFile(event.target.files?.[0] || null)}
                                />
                            </label>
                        </div>
                    </div>

                    <label className="merchant-branding__colour-field">
                        <span className="merchant-branding__value merchant-branding__value--strong">Primary colour</span>
                        <div className="merchant-branding__colour-control">
                            <input
                                className="merchant-branding__colour-picker"
                                type="color"
                                value={primaryColour}
                                onChange={(event) => setPrimaryColour(event.target.value.toUpperCase())}
                                aria-label="Choose primary colour"
                            />
                            <input
                                className="merchant-branding__colour-value"
                                type="text"
                                value={primaryColour}
                                onChange={(event) => setPrimaryColour(event.target.value)}
                                aria-label="Primary colour hex value"
                            />
                        </div>
                    </label>

                    <button
                        type="button"
                        className="merchant-details-btn merchant-details-btn--dark merchant-branding__save"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </section>
        </div>
    )
}

export default function MerchantDetailsPage() {
    const { id: merchantId } = useParams()
    const [activeTab, setActiveTab] = useState('overview')
    const [isEditMerchantOpen, setIsEditMerchantOpen] = useState(false)
    const [outletList, setOutletList] = useState(null)
    const [userInfo, setUserInfo] = useState(null)
    const [notice, setNotice] = useState(null)
    const [pageError, setPageError] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_OUTLET_PAGE_SIZE)
    const [merchantData, setMerchantData] = useState(null)
    const [stateOptions, setStateOptions] = useState(null)
    const [countryOptions, setCountryOptions] = useState(null)
    const [categoryOptions, setCategoryOptions] = useState(null)
    const [categoryStatusOptions, setCategoryStatusOptions] = useState(null)
    const [initialLoading, setInitialLoading] = useState(true)
    const [outletLoading, setOutletLoading] = useState(false)
    const [catalogRows, setCatalogRows] = useState([])
    const [catalogPagination, setCatalogPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0,
    })
    const [catalogLoading, setCatalogLoading] = useState(false)
    const [brandingData, setBrandingData] = useState(null)
    const [brandingLoading, setBrandingLoading] = useState(false)
    const [filterValues, setFilterValues] = useState({ category: '', status: '' })
    const [searchQuery, setSearchQuery] = useState('')
    // permission states
    const [editable, setEditable] = useState(true)

    const navigate = useNavigate()

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return
        setCurrentPage(page)
    }

    useEffect(() => {
        let mounted = true

        const loadInitialData = async () => {
            setInitialLoading(true)
            await Promise.all([loadUserData(), loadMerchantData()])
            if (mounted) {
                setInitialLoading(false)
            }
        }

        loadInitialData()

        return () => {
            mounted = false
        }
    }, [merchantId])

    useEffect(() => {
        loadOutletData(currentPage)
    }, [currentPage, merchantId])

    useEffect(() => {
        if (activeTab === 'branding') {
            loadBrandingData()
        }
    }, [activeTab, merchantId])

    useEffect(() => {
        if (activeTab !== 'catalog') return undefined

        const timeoutId = window.setTimeout(() => {
            loadCatalogData(1)
        }, searchQuery ? 350 : 0)

        return () => window.clearTimeout(timeoutId)
    }, [activeTab, merchantId, searchQuery, filterValues.category, filterValues.status])

    const loadOutletData = async (page = currentPage) => {
        setOutletLoading(true)
        try {
            const response = await merchantApi.merchants.outlets(merchantId, {
                per_page: pageSize,
                page,
            })
            const normalized = normalizeOutletList(response)
            setOutletList(normalized)
            setPageSize(normalized.perPage || DEFAULT_OUTLET_PAGE_SIZE)
            setTotalPages(normalized.totalPages || 1)
        } catch (error) {
            setPageError(error.message || 'Error fetching data.')
        } finally {
            setOutletLoading(false)
        }
    }

    const loadUserData = async () => {
        try {
            const response = await merchantApi.auth.me();
            // if (!response?.data?.permissions?.includes(`view-${moduleCode}`)) {
            //     navigate('/unauthorized')
            //     return
            // }
            const generalData = await merchantApi.general.get()
            setUserInfo(response?.data || null)
            setStateOptions(generalData?.data?.states || [])
            setCountryOptions(generalData?.data?.countries || [])
            setCategoryOptions(generalData?.data?.catalog_categories || [])
            setCategoryStatusOptions(generalData?.data?.catalog_statuses || [])
            // setEditable(response?.data?.permissions?.includes(`edit-${moduleCode}`))
        } catch (error) {
            setPageError(error.message || 'Error fetching data.')
        }
    }

    const loadMerchantData = async () => {
        try {
            const response = await merchantApi.merchants.show(merchantId)
            setMerchantData(normalizeMerchantDetail(response))
            pageError && setPageError(null)
        } catch (error) {
            setPageError(error.message || 'Error fetching data.')
        }
    }

    const handleMerchantSaved = async () => {
        await loadMerchantData()
        setNotice('Merchant details updated successfully.')
    }

    const loadCatalogData = async (page = 1) => {
        setCatalogLoading(true)

        try {
            const response = await merchantApi.merchants.catalog(merchantId, {
                page,
                per_page: catalogPagination.per_page,
                search: searchQuery.trim(),
                category: filterValues.category,
                status: filterValues.status,
            })
            const normalized = normalizeCollection(response)
            setCatalogRows(normalized.rows)
            setCatalogPagination(normalized.pagination)
        } catch (error) {
            setPageError(error?.message || 'Unable to load merchant catalog.')
        } finally {
            setCatalogLoading(false)
        }
    }

    const loadBrandingData = async () => {
        setBrandingLoading(true)

        try {
            const response = await merchantApi.merchants.branding.get(merchantId)
            setBrandingData(response?.data || null)
        } catch (error) {
            setPageError(error?.message || 'Unable to load merchant branding.')
        } finally {
            setBrandingLoading(false)
        }
    }

    const tabList = [
        { key: 'overview', label: 'Overview' },
        { key: 'outlets', label: 'Outlets' },
        { key: 'catalog', label: 'Catalog' },
        // { key: 'orders', label: 'Orders' },
        // { key: 'reservations', label: 'Reservations' },
        // { key: 'payments', label: 'Payments' },
        { key: 'branding', label: 'Branding' },
    ]
    
    return (
        <div className="merchant-details-page">
            <Header
                items={[
                    { label: 'Cuscapi', to: '/' },
                    { label: 'Merchant details', current: true },
                ]}
            />
            <div className="merchant-details-page__topbar">
                {merchantData?.logo_url ? (
                    <img
                        src={merchantData.logo_url}
                        className="merchant-details-page__logo"
                        alt={`${merchantData.name || 'Merchant'} logo`}
                    />
                ) : (
                    <div className="merchant-details-page__logo-icon">
                        <Icon icon="ph:storefront" height={24} />
                    </div>
                )}
                <div className="">
                    <div className="flex-container">
                        <span className="merchant-details-page__title">{merchantData?.name || 'Merchant'}</span>
                        <div className="merchant-details-display-field__value">
                            <span className="merchant-details-badge-id">{merchantData?.merchant_code || '-'}</span>
                        </div>
                        <Box className={`merchant-details-chip merchant-details-chip--${merchantData?.status || 'inactive'}`}>
                            <Icon icon="ph:circle-fill" height={5} />
                            {merchantData?.status ? merchantData?.status[0].toUpperCase() + merchantData?.status.slice(1) : '-'}
                        </Box>
                    </div>
                    <Box className="flex-container" mt={"2px"}>
                        <span>{merchantData?.outlets_count || 0} outlets</span>
                        <Icon icon="ph:circle-fill" height={5} />
                        <span>{merchantData?.city || '-'}</span>
                        <Icon icon="ph:circle-fill" height={5} />
                        <span>
                            Joined {merchantData?.joined_at
                                ? moment(merchantData.joined_at).format('DD/MM/YYYY')
                                : '-'}
                        </span>
                    </Box>
                </div>
            </div>

            {notice && <div className="sr-success">{notice}</div>}
            {pageError && <div className="sr-error">{pageError}</div>}

            <div className="merchant-details-tabs">
                {
                    tabList.map((tab) => (
                        <button
                            className={`merchant-details-tab ${activeTab === tab.key ? 'merchant-details-tab--active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))
                }
            </div>

            {initialLoading ? (
                <div className="merchant-details-subcontainer">
                    <div className="merchant-details-loading">Loading store setup...</div>
                </div>
            ) : (
                <>
                    {activeTab === 'overview' && (
                        <OverviewTab merchantData={merchantData} onEditMerchant={() => setIsEditMerchantOpen(true)} />
                    )}
                    {activeTab === 'outlets' && (
                        <OutletsTab
                            onAddOutlet={() => navigate('/outlet-setup/add')}
                            outletList={outletList}
                            handlePageChange={handlePageChange}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            navigate={navigate}
                            loading={outletLoading}
                            editable={editable}
                        />
                    )}
                    {
                        activeTab === 'catalog' && (
                            <CatalogTab
                                filterValues={filterValues}
                                setFilterValues={setFilterValues}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                rows={catalogRows}
                                pagination={catalogPagination}
                                loading={catalogLoading}
                                onPageChange={loadCatalogData}
                                categoryOptions={categoryOptions}
                                categoryStatusOptions={categoryStatusOptions}
                            />
                        )
                    }
                    {
                        activeTab === 'orders' && (
                            <OrdersTab
                                filterValues={filterValues}
                                setFilterValues={setFilterValues}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                            />
                        )
                    }
                    {
                        activeTab === 'reservations' && (
                            <ReservationTab
                                filterValues={filterValues}
                                setFilterValues={setFilterValues}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                            />
                        )
                    }
                    {
                        activeTab === 'payments' && (
                            <PaymentTab
                                filterValues={filterValues}
                                setFilterValues={setFilterValues}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                            />
                        )
                    }
                    {
                        activeTab === 'branding' && (
                            brandingLoading ? (
                                <div className="merchant-details-loading">Loading branding...</div>
                            ) : (
                                <BrandingTab
                                    merchantData={merchantData}
                                    brandingData={brandingData}
                                    merchantId={merchantId}
                                    onSaved={async () => {
                                        await Promise.all([loadMerchantData(), loadBrandingData()])
                                        setNotice('Merchant branding updated successfully.')
                                    }}
                                />
                            )
                        )
                    }
                </>
            )}

            {isEditMerchantOpen && (
                <EditMerchantDrawer
                    merchantData={merchantData}
                    stateOptions={stateOptions}
                    countryOptions={countryOptions}
                    onSaved={handleMerchantSaved}
                    onClose={() => setIsEditMerchantOpen(false)}
                />
            )}
        </div>
    )
}
